import moment from 'moment';

const SLOT_MINUTES = 30;
const MIN_EVENT_MINUTES = 15;

export default {
  data() {
    return {
      slotMinutes: SLOT_MINUTES,
      minuteHeight: 6.0,
      minEventMinutes: MIN_EVENT_MINUTES,
    };
  },
  computed: {
    calendarBase() {
      if (this.hasDays) {
        const [firstDay] = this.days;
        if (firstDay && firstDay.date) {
          const base = moment(firstDay.date);
          if (base.isValid()) {
            return base.clone().startOf('day');
          }
        }
      }
      return this.nowMoment.clone().startOf('day');
    },
    timeRange() {
      let minHour = 12;
      let maxHour = 24;

      if (this.hasDays && this.days.length > 0) {
        const allTimes = [];

        this.days.forEach((day) => {
          if (day.appointments && day.appointments.length > 0) {
            day.appointments.forEach((appointment) => {
              if (appointment.startDate) {
                const start = moment(appointment.startDate);
                if (start.isValid()) {
                  const hour = start.hour();
                  const minute = start.minute();
                  allTimes.push(hour * 60 + minute);
                }
              }
              if (appointment.endDate) {
                const end = moment(appointment.endDate);
                if (end.isValid()) {
                  const hour = end.hour();
                  const minute = end.minute();
                  allTimes.push(hour * 60 + minute);
                }
              }
            });
          }
        });

        if (allTimes.length > 0) {
          const minMinutes = Math.min(...allTimes);
          const maxMinutes = Math.max(...allTimes);

          const calculatedMinHour = Math.floor(minMinutes / 60);
          const calculatedMaxHour = Math.ceil(maxMinutes / 60);

          if (calculatedMinHour < 12) {
            minHour = calculatedMinHour;
          } else if (calculatedMinHour === 12) {
            minHour = 12;
          } else {
            minHour = calculatedMinHour - 1;
          }

          maxHour = Math.max(calculatedMaxHour, 12);
        } else {
          minHour = 12;
          maxHour = 18;
        }
      } else {
        minHour = 12;
        maxHour = 18;
      }

      return { minHour, maxHour };
    },
    calendarStart() {
      const base = this.calendarBase.clone();
      return base.hour(this.timeRange.minHour).minute(0).second(0);
    },
    calendarEnd() {
      const base = this.calendarBase.clone();
      return base.hour(this.timeRange.maxHour).minute(0).second(0);
    },
    totalMinutes() {
      const { minHour, maxHour } = this.timeRange;
      return (maxHour - minHour) * 60;
    },
    calendarHeight() {
      return this.totalMinutes * this.minuteHeight;
    },
    slotHeight() {
      return this.slotMinutes * this.minuteHeight;
    },
    timeSlots() {
      const slots = [];
      const count = Math.ceil(this.totalMinutes / this.slotMinutes);
      for (let i = 0; i < count; i += 1) {
        const slotMoment = this.calendarStart.clone().add(i * this.slotMinutes, 'minutes');
        slots.push({
          key: slotMoment.format('HHmm'),
          label: slotMoment.minute() === 0 || slotMoment.minute() === 30
            ? slotMoment.format('HH:mm')
            : '',
          isHalf: slotMoment.minute() !== 0 && slotMoment.minute() !== 30,
        });
      }
      return slots;
    },
    dayEventMap() {
      if (!this.hasDays) {
        return {};
      }
      return this.days.reduce((result, day) => {
        result[day.date] = this.buildDayEvents(day);
        return result;
      }, {});
    },
    currentTimeStyle() {
      if (!this.hasDays) {
        return null;
      }

      const today = moment().startOf('day');
      const hasToday = this.days.some(day => moment(day.date).isSame(today, 'day'));
      if (!hasToday) {
        return null;
      }

      const dayStart = this.nowMoment.clone().hour(this.timeRange.minHour).minute(0).second(0);
      const minutesOfDay = this.nowMoment.diff(dayStart, 'minutes');
      const clampedMinutes = Math.min(Math.max(minutesOfDay, 0), this.totalMinutes);
      const offset = clampedMinutes * this.minuteHeight;
      return {
        top: `${offset}px`,
      };
    },
  },
  methods: {
    buildDayEvents(day) {
      if (!day || !Array.isArray(day.appointments) || day.appointments.length === 0) {
        return [];
      }

      const rangeStart = moment(day.date).hour(this.timeRange.minHour).minute(0).second(0);
      const rangeEnd = moment(day.date).hour(this.timeRange.maxHour).minute(0).second(0);
      if (!rangeStart.isValid()) {
        return [];
      }
      const defaultSlotMinutes = this.getDefaultSlotMinutes(day.appointments);
      const events = day.appointments
        .map((appointment, index) => {
          const start = appointment.startDate ? moment(appointment.startDate) : null;
          if (!start || !start.isValid()) {
            return null;
          }
          const originalEnd = appointment.endDate ? moment(appointment.endDate) : null;
          const isStartDateEqualToEndDate = originalEnd && originalEnd.isValid() && start.isSame(originalEnd, 'minute');
          let end = originalEnd;
          if (!end || !end.isValid() || !end.isAfter(start)) {
            end = start.clone().add(defaultSlotMinutes, 'minutes');
          }
          const key = appointment.id
            || appointment.key
            || `${day.date}-${index}`;
          return {
            appointment,
            start,
            end,
            originalEnd: originalEnd && originalEnd.isValid() ? originalEnd : null,
            isStartDateEqualToEndDate,
            key: `event-${key}`,
          };
        })
        .filter(item => item);
      if (events.length === 0) {
        return [];
      }
      events.sort((a, b) => a.start.valueOf() - b.start.valueOf());
      const clusters = [];
      let cluster = [];
      let clusterEnd = null;
      events.forEach((event) => {
        if (cluster.length === 0) {
          cluster.push(event);
          clusterEnd = event.end.clone();
          return;
        }
        if (event.start.isBefore(clusterEnd)) {
          cluster.push(event);
          if (event.end.isAfter(clusterEnd)) {
            clusterEnd = event.end.clone();
          }
        } else {
          clusters.push(cluster);
          cluster = [event];
          clusterEnd = event.end.clone();
        }
      });
      if (cluster.length > 0) {
        clusters.push(cluster);
      }
      const rangeMinutes = rangeEnd.diff(rangeStart, 'minutes');
      const processedEvents = events.map((event) => {
        const startMinutes = event.start.diff(rangeStart, 'minutes');
        const endMinutes = event.end.diff(rangeStart, 'minutes');
        const clampedStart = startMinutes < 0 ? 0 : startMinutes;
        const clampedEnd = endMinutes > rangeMinutes ? rangeMinutes : endMinutes;
        const duration = clampedEnd - clampedStart;
        const eventTop = clampedStart * this.minuteHeight;
        const eventBottom = eventTop + (duration * this.minuteHeight);

        return {
          event,
          clampedStart,
          clampedEnd,
          duration,
          eventTop,
          eventBottom,
        };
      });

      const groups = [];
      const processedSet = new Set();

      const findOverlappingGroup = (event, currentGroups) => {
        for (let i = 0; i < currentGroups.length; i += 1) {
          const group = currentGroups[i];
          const hasOverlap = group.some((groupEvent) => {
            const overlaps = (
              event.eventTop < groupEvent.eventBottom
              && event.eventBottom > groupEvent.eventTop
            );
            return overlaps;
          });
          if (hasOverlap) {
            return i;
          }
        }
        return -1;
      };

      processedEvents.forEach((current) => {
        if (processedSet.has(current)) {
          return;
        }

        const groupIndex = findOverlappingGroup(current, groups);
        if (groupIndex >= 0) {
          groups[groupIndex].push(current);
        } else {
          groups.push([current]);
        }
        processedSet.add(current);
      });

      const groupsData = groups.map((group, groupIndex) => {
        const minStartTime = moment.min(group.map(g => g.event.start));
        const endTimes = group.map((g) => {
          if (g.event.originalEnd && g.event.originalEnd.isValid()) {
            return g.event.originalEnd;
          }
          return g.event.end;
        });
        const maxEndTime = moment.max(endTimes);
        const minStartMinutes = minStartTime.diff(rangeStart, 'minutes');
        const maxEndMinutes = maxEndTime.diff(rangeStart, 'minutes');
        const clampedMinStart = minStartMinutes < 0 ? 0 : minStartMinutes;
        const clampedMaxEnd = maxEndMinutes > rangeMinutes ? rangeMinutes : maxEndMinutes;
        const originalTop = clampedMinStart * this.minuteHeight;
        const maxBottom = clampedMaxEnd * this.minuteHeight;
        let groupHeight = maxBottom - originalTop;

        const sortedGroup = [...group].sort((a, b) => {
          const startDiff = a.clampedStart - b.clampedStart;
          if (Math.abs(startDiff) < 1) {
            return a.duration - b.duration;
          }
          if (startDiff !== 0) {
            return startDiff;
          }
          return a.duration - b.duration;
        });

        const eventsWithSameDate = sortedGroup.filter(p => p.event.isStartDateEqualToEndDate);
        const eventsWithoutSameDate = sortedGroup.filter(p => !p.event.isStartDateEqualToEndDate);

        if (eventsWithoutSameDate.length === 0 && eventsWithSameDate.length > 0) {
          const minHeight = eventsWithSameDate.length * 45;
          if (groupHeight < minHeight) {
            groupHeight = minHeight;
          }
        }

        const heightForSameDateEvents = eventsWithSameDate.length * 45;
        const remainingHeight = groupHeight - heightForSameDateEvents;
        const heightPerOtherEvent = eventsWithoutSameDate.length > 0
          ? remainingHeight / eventsWithoutSameDate.length
          : 0;

        const groupEvents = sortedGroup.map((processed, index) => {
          const { event } = processed;

          let eventHeight;
          if (event.isStartDateEqualToEndDate) {
            eventHeight = 45;
          } else {
            eventHeight = heightPerOtherEvent;
          }

          const style = {
            height: `${eventHeight}px`,
            width: '100%',
          };

          if (index > 0) {
            style.marginTop = '0px';
          }

          return {
            appointment: event.appointment,
            key: event.key,
            style,
            date: day.date,
          };
        });

        return {
          key: `group-${groupIndex}`,
          originalTop,
          groupHeight,
          style: {
            position: 'absolute',
            top: `${originalTop}px`,
            height: `${groupHeight}px`,
            left: '0%',
            width: '100%',
          },
          events: groupEvents,
        };
      });

      let accumulatedOffset = 0;
      return groupsData.map((groupData, groupIndex) => {
        if (groupIndex > 0) {
          const previousGroup = groupsData[groupIndex - 1];
          const previousBottom = previousGroup.originalTop
            + previousGroup.groupHeight
            + accumulatedOffset;
          if (groupData.originalTop < previousBottom) {
            accumulatedOffset += previousBottom - groupData.originalTop;
          }
        }

        return {
          key: groupData.key,
          style: {
            ...groupData.style,
            top: `${groupData.originalTop + accumulatedOffset}px`,
          },
          events: groupData.events,
        };
      });
    },
    getDefaultSlotMinutes(appointments) {
      if (!Array.isArray(appointments) || appointments.length < 2) {
        return this.slotMinutes;
      }
      const moments = appointments
        .map(item => (item.startDate ? moment(item.startDate) : null))
        .filter(item => item && item.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf());
      if (moments.length < 2) {
        return this.slotMinutes;
      }
      let minDiff = null;
      for (let i = 1; i < moments.length; i += 1) {
        const diff = moments[i].diff(moments[i - 1], 'minutes');
        if (diff > 0 && (minDiff === null || diff < minDiff)) {
          minDiff = diff;
        }
      }
      return minDiff || this.slotMinutes;
    },
  },
};
